import { parseAdsenseGlobalScript } from "@/lib/adsense";

export type AdProvider = "adsense" | "gam" | "monetag" | "ezoic" | "mediavine";

export type AdSlotKey =
  | "articleTopBanner"
  | "articleSidebar"
  | "articleInlineMobile"
  | "articleMiddleBanner"
  | "articleBottomBanner";

export type AdNetworkSettings = {
  adsense: {
    enabled: boolean;
    clientId: string;
    scriptUrl: string;
    globalScriptCode: string;
  };
  gam: {
    enabled: boolean;
    networkCode: string;
    scriptUrl: string;
  };
  monetag: {
    enabled: boolean;
    scriptUrl: string;
  };
  ezoic: {
    enabled: boolean;
    scriptUrl: string;
  };
  mediavine: {
    enabled: boolean;
    siteId: string;
    scriptUrl: string;
  };
};

export type AdSlotConfig = {
  key: AdSlotKey;
  name: string;
  description: string;
  formatLabel: string;
  enabled: boolean;
  provider: AdProvider;
  slotCode: string;
  containerId: string;
  secondaryCode: string;
};

export type AdSettings = {
  lazyLoadEnabled: boolean;
  providers: AdNetworkSettings;
  slots: Record<AdSlotKey, AdSlotConfig>;
};

export const AD_PROVIDER_OPTIONS: { value: AdProvider; label: string; hint: string }[] = [
  { value: "adsense", label: "Google AdSense", hint: "data-ad-client + data-ad-slot" },
  { value: "gam", label: "Google Ad Manager", hint: "Ad unit path + div container" },
  { value: "monetag", label: "Monetag", hint: "Zone/script por posição" },
  { value: "ezoic", label: "Ezoic", hint: "Placeholder gerenciado" },
  { value: "mediavine", label: "Mediavine", hint: "Placeholder gerenciado" },
];

export const AD_SLOT_CATALOG: Record<AdSlotKey, Pick<AdSlotConfig, "key" | "name" | "description" | "formatLabel">> = {
  articleTopBanner: {
    key: "articleTopBanner",
    name: "Artigo • Topo",
    description: "Banner above the fold, logo após o resumo rápido.",
    formatLabel: "720×290",
  },
  articleSidebar: {
    key: "articleSidebar",
    name: "Artigo • Sidebar",
    description: 'Bloco 300x250 abaixo de "Neste Artigo" no desktop.',
    formatLabel: "300×250",
  },
  articleInlineMobile: {
    key: "articleInlineMobile",
    name: "Artigo • In-content mobile",
    description: "Bloco móvel entre o primeiro conteúdo e a continuidade da leitura.",
    formatLabel: "300×250",
  },
  articleMiddleBanner: {
    key: "articleMiddleBanner",
    name: "Artigo • Meio do conteúdo",
    description: "Banner entre seções após ~45% do artigo.",
    formatLabel: "720×290",
  },
  articleBottomBanner: {
    key: "articleBottomBanner",
    name: "Artigo • Rodapé do conteúdo",
    description: "Banner antes da caixa da equipe/autoria.",
    formatLabel: "720×90",
  },
};

export const defaultAdSettings: AdSettings = {
  lazyLoadEnabled: true,
  providers: {
    adsense: {
      enabled: true,
      clientId: "",
      scriptUrl: "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js",
      globalScriptCode: "",
    },
    gam: {
      enabled: false,
      networkCode: "",
      scriptUrl: "https://securepubads.g.doubleclick.net/tag/js/gpt.js",
    },
    monetag: {
      enabled: false,
      scriptUrl: "",
    },
    ezoic: {
      enabled: false,
      scriptUrl: "",
    },
    mediavine: {
      enabled: false,
      siteId: "",
      scriptUrl: "",
    },
  },
  slots: Object.fromEntries(
    Object.values(AD_SLOT_CATALOG).map((slot) => [
      slot.key,
      {
        ...slot,
        enabled: true,
        provider: "adsense" as AdProvider,
        slotCode: "",
        containerId: slot.key,
        secondaryCode: "",
      },
    ])
  ) as Record<AdSlotKey, AdSlotConfig>,
};

export function normalizeAdSettings(value: Partial<AdSettings> | null | undefined): AdSettings {
  const incomingSlots = value?.slots ?? {};
  const slots = Object.fromEntries(
    (Object.keys(AD_SLOT_CATALOG) as AdSlotKey[]).map((slotKey) => {
      const catalog = AD_SLOT_CATALOG[slotKey];
      const next = incomingSlots[slotKey];

      return [
        slotKey,
        {
          ...defaultAdSettings.slots[slotKey],
          ...catalog,
          ...next,
          key: slotKey,
          name: catalog.name,
          description: catalog.description,
          formatLabel: catalog.formatLabel,
          containerId: next?.containerId?.trim() || defaultAdSettings.slots[slotKey].containerId,
          slotCode: next?.slotCode?.trim() || "",
          secondaryCode: next?.secondaryCode?.trim() || "",
        },
      ];
    })
  ) as Record<AdSlotKey, AdSlotConfig>;

  const rawAdsenseSettings = {
    ...defaultAdSettings.providers.adsense,
    ...value?.providers?.adsense,
    clientId: value?.providers?.adsense?.clientId?.trim() ?? defaultAdSettings.providers.adsense.clientId,
    scriptUrl: value?.providers?.adsense?.scriptUrl?.trim() ?? defaultAdSettings.providers.adsense.scriptUrl,
    globalScriptCode: value?.providers?.adsense?.globalScriptCode?.trim() ?? "",
  };

  const parsedGlobalScript = parseAdsenseGlobalScript(rawAdsenseSettings.globalScriptCode);

  return {
    ...defaultAdSettings,
    ...value,
    providers: {
      ...defaultAdSettings.providers,
      ...value?.providers,
      adsense: {
        ...rawAdsenseSettings,
        clientId: rawAdsenseSettings.clientId || parsedGlobalScript?.clientId || "",
        scriptUrl: parsedGlobalScript?.src || rawAdsenseSettings.scriptUrl || defaultAdSettings.providers.adsense.scriptUrl,
      },
      gam: {
        ...defaultAdSettings.providers.gam,
        ...value?.providers?.gam,
      },
      monetag: {
        ...defaultAdSettings.providers.monetag,
        ...value?.providers?.monetag,
      },
      ezoic: {
        ...defaultAdSettings.providers.ezoic,
        ...value?.providers?.ezoic,
      },
      mediavine: {
        ...defaultAdSettings.providers.mediavine,
        ...value?.providers?.mediavine,
      },
    },
    slots,
    lazyLoadEnabled: value?.lazyLoadEnabled ?? defaultAdSettings.lazyLoadEnabled,
  };
}
